import supabase from '../supabaseClient';

/**
 * Hash email for privacy-preserving error logging
 * Uses simple hash - in production, consider using a proper hashing library
 */
async function hashEmail(email) {
  if (!email) return null;

  try {
    // Simple hash using built-in crypto API
    const encoder = new TextEncoder();
    const data = encoder.encode(email.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    console.warn('Failed to hash email:', err);
    return null;
  }
}

/**
 * Get browser information for error context
 */
function getBrowserInfo() {
  try {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  } catch (err) {
    return {};
  }
}

/**
 * Log an error to the Supabase error_logs table
 *
 * @param {Object} options - Error logging options
 * @param {Error|string} options.error - The error object or message
 * @param {string} options.source - Source of the error (e.g., 'onboarding', 'auth', 'profile')
 * @param {string} [options.action] - Specific action being performed (e.g., 'signup', 'upsert_profile')
 * @param {string} [options.stepId] - Onboarding step identifier
 * @param {number} [options.currentStep] - Current step number
 * @param {string} [options.authUserId] - Authenticated user ID
 * @param {string} [options.userEmail] - User email (will be hashed)
 * @param {Object} [options.metadata] - Additional context data
 * @returns {Promise<{success: boolean, error?: Error}>}
 */
export async function logError({
  error,
  source,
  action = null,
  stepId = null,
  currentStep = null,
  authUserId = null,
  userEmail = null,
  metadata = null,
}) {
  try {
    // Extract error details
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : null;
    const errorCode = error?.code || error?.error_code || null;

    // Hash email for privacy
    const userEmailHash = await hashEmail(userEmail);

    // Get browser context
    const browserInfo = getBrowserInfo();

    // Prepare log entry
    const logEntry = {
      error_message: errorMessage,
      error_stack: errorStack,
      error_code: errorCode,
      source,
      action,
      step_id: stepId,
      current_step: currentStep,
      auth_user_id: authUserId,
      user_email_hash: userEmailHash,
      user_agent: navigator.userAgent || null,
      browser_info: browserInfo,
      metadata: metadata ? JSON.parse(JSON.stringify(metadata)) : null, // ensure serializable
    };

    // Insert to Supabase
    const { error: insertError } = await supabase
      .from('error_logs')
      .insert(logEntry);

    if (insertError) {
      // Don't throw - we don't want error logging to break the app
      console.error('Failed to log error to database:', insertError);
      return { success: false, error: insertError };
    }

    return { success: true };
  } catch (err) {
    // Catch-all: if error logging itself fails, just log to console
    console.error('Error in logError function:', err);
    return { success: false, error: err };
  }
}

/**
 * Convenience wrapper for logging onboarding errors
 *
 * @param {Object} options - Onboarding error options
 * @param {Error|string} options.error - The error
 * @param {string} options.action - Action being performed
 * @param {string} [options.stepId] - Step identifier
 * @param {number} [options.currentStep] - Step number
 * @param {string} [options.authUserId] - User ID
 * @param {string} [options.userEmail] - User email
 * @param {Object} [options.metadata] - Additional context
 */
export async function logOnboardingError({
  error,
  action,
  stepId = null,
  currentStep = null,
  authUserId = null,
  userEmail = null,
  metadata = null,
}) {
  return logError({
    error,
    source: 'onboarding',
    action,
    stepId,
    currentStep,
    authUserId,
    userEmail,
    metadata,
  });
}
